import { ref, type Ref } from 'vue';
import { markRaw, type Component } from 'vue';
import itemRankingService, { type ItemRanking } from '@/services/itemRanking.service';
import itemRankingGroupService from '@/services/itemRankingGroup.service';
import itemRankingDescriptionService, { type ItemRankingDescription } from '@/services/itemRankingDescription.service';
import { useModalHelper } from '@/composables/modalHelper';
import EditItemRankingModal from '@/modals/EditItemRankingModal.vue';

// Port a Vue de src/frontend/commons/factory/ranking.factory.js: decide si un item jerarquizado
// debe verse para el profile actual. Conviven dos sistemas (ver profile.api.js): el nuevo, basado
// en rol/plan (`profile.rolesRankingItems`, resuelto sin fetch), y el viejo, basado en rankings de
// texto jerárquicos (`profile.rankings` + el array completo de `itemRanking`, que hay que traer).

// promesa cacheada a nivel de módulo con el array legacy de itemRanking, análoga a
// $rootScope.itemRankings de mobile/app.js — se resuelve una sola vez por sesión
let legacyItemRankingsPromise: Promise<ItemRanking[]> | null = null;

// trae (una sola vez) el array legacy de itemRanking de la enterprise actual
function loadLegacyItemRankings(): Promise<ItemRanking[]> {
  if (!legacyItemRankingsPromise) {
    legacyItemRankingsPromise = itemRankingService.queryNoPaginate({ enterprise: window.profile?.enterprise });
  }
  return legacyItemRankingsPromise;
}

// función pura: resuelve si el item es visible para el profile actual. itemDefault es la
// visibilidad cuando no hay ninguna configuración para el item (equivalente a !item-hidden)
export function isItemRankingVisible(itemName: string, itemDefault: boolean, legacyItemRankings?: ItemRanking[]): boolean {
  const profile = window.profile;
  if (profile?.rolesRankingItems) {
    const entry = profile.rolesRankingItems.find((f: any) => f.item === itemName);
    return entry ? entry.enabled : itemDefault;
  }
  if (!profile?.rankings || profile.rankings.length === 0) return itemDefault;
  if (!legacyItemRankings) return itemDefault;
  const myItemRanking = legacyItemRankings.find(f => f.item === itemName);
  const myRankings = myItemRanking?.rankings ?? [];
  if (myRankings.length === 0) return itemDefault;
  return window.rankingUtils.matchItemRanking(profile, myRankings);
}

// composable: visibilidad reactiva de un item para el profile actual (resuelve sincrónicamente
// con el sistema nuevo; si hace falta el fallback legacy, espera el fetch cacheado)
export function useItemRanking(itemName: string, itemDefault: boolean): { visible: Ref<boolean>; isSysadmin: boolean } {
  const visible = ref(isItemRankingVisible(itemName, itemDefault));
  const isSysadmin = !!window.profile?.sysadmin;
  if (!window.profile?.rolesRankingItems) {
    loadLegacyItemRankings()
      .then(legacyItemRankings => { visible.value = isItemRankingVisible(itemName, itemDefault, legacyItemRankings); })
      .catch(err => console.error(err));
  }
  return { visible, isSysadmin };
}

// true si el item tiene una jerarquización configurada (nombre + grupo/s asociados), equivalente
// a isConfigurated() de la directiva Angular para el sistema nuevo
export function isItemConfigured(itemName: string, allDescriptions: ItemRankingDescription[]): boolean {
  const description = allDescriptions.find(d => d.rankingItem === itemName);
  return !!description && (!!description.rankingGroup?._id || !!description.rankingGroups?.length);
}

// abre el modal de configuración del item (mismo componente y flujo que RolesPanel.vue#editItem):
// busca (o arma) su itemRankingDescription, deja elegir nombre + grupos, y persiste el resultado.
// Devuelve la description resultante (o null si se canceló) para que el llamador actualice su UI.
export async function openItemRankingConfigModal(itemName: string, itemDefault: boolean): Promise<ItemRankingDescription | null> {
  const [allGroups, existingDescriptions] = await Promise.all([
    itemRankingGroupService.queryNoPaginate({}),
    itemRankingDescriptionService.queryNoPaginate({ rankingItem: itemName }),
  ]);
  const description: ItemRankingDescription = existingDescriptions[0] ?? {
    rankingItem: itemName,
    name: '',
    visibleByDefault: itemDefault,
    rankingGroup: undefined as any,
    rankingGroups: [],
  };

  const { open } = useModalHelper<{ name: string; rankingGroups: { _id: string; name: string }[] } | null>(
    markRaw<Component>(EditItemRankingModal),
    { description, allGroups },
    { title: window.i18next.t('Editar ítem'), lateral: false },
  );
  const result = await open();
  if (!result) return null;

  description.name = result.name;
  description.rankingGroups = result.rankingGroups;
  description.rankingGroup = result.rankingGroups[0];
  return itemRankingDescriptionService.save(description);
}
